import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import './OnCallReportsIndividualPupil.css';
import './OnCallReportGroupSummary.css';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { SEC_ApiCall } from '../func.js';
import { PieChartComponent } from './chartComponents/PieChartComponent';
import { findSchoolPeriod } from './OnCallFunc.js';
import { OnCallCompleteCall } from './OnCallCompleteCall.js';
import { LessonTimesCountTable } from './tableComponents/LessonTimesCountTable.js';
import { WeeklyCallTrendsLineChart } from './chartComponents/WeeklyCallTrendsLineChart.js';
import OnCallReportDateFilter from './OnCallReportDateFilter.js';
import { PupilGroupIcons } from './tableComponents/PupilCallTypeSummaryTable.js';


function formatDateForInput(dateValue) {
    return dateValue.toISOString().split('T')[0];
}


function getDefaultAcademicYearDates() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const septemberFirstThisYear = new Date(currentYear, 8, 1);
    const startDate = today >= septemberFirstThisYear
        ? septemberFirstThisYear
        : new Date(currentYear - 1, 8, 1);

    return {
        startDate: formatDateForInput(startDate),
        endDate: formatDateForInput(today),
    };
}


function normaliseReportResponse(response) {
    if (typeof response === 'string') {
        try {
            return JSON.parse(response);
        } catch (error) {
            console.error('Failed to parse individual pupil report payload string', error);
            return {};
        }
    }

    if (response && typeof response?.body === 'string') {
        try {
            return JSON.parse(response.body);
        } catch (error) {
            console.error('Failed to parse individual pupil report response body', error);
            return {};
        }
    }

    return response && typeof response === 'object' ? response : {};
}


export default function OnCallReportsIndividualPupil() {
    const navigate = useNavigate();
    const location = useLocation();
    const { pupilId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultDates = getDefaultAcademicYearDates();

    const [dataState, setDataState] = useState(null);
    const [isLoadedState, setIsLoadedState] = useState(false);
    const [isApplyingDateFilterState, setIsApplyingDateFilterState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(false);
    const [filteredDataState, setFilteredDataState] = useState([]);
    const [selectedLesson, setSelectedLesson] = useState(null);
    const [selectedCallType, setSelectedCallType] = useState(null);
    const [totalCallCountState, setTotalCallCountState] = useState(0);
    const [allCallsArrayState, setAllCallsArrayState] = useState([]);
    const [callTypeColoursState, setCallTypeColoursState] = useState([]);
    const [selectedTermRangeState, setSelectedTermRangeState] = useState(null);
    const [startDateState, setStartDateState] = useState(searchParams.get('startDate') || defaultDates.startDate);
    const [endDateState, setEndDateState] = useState(searchParams.get('endDate') || defaultDates.endDate);

    const backPath = location.state?.backPath || '/app/oncall/reports';

    function resetFilters(sourceData) {
        const allRecords = sourceData?.onCallRecords || [];
        setSelectedLesson(null);
        setSelectedCallType(null);
        setFilteredDataState(allRecords);
        setAllCallsArrayState(allRecords);
        setTotalCallCountState(allRecords.length);
    }

    function handleLessonSelect(selectedPeriod) {
        if (!dataState) {
            return;
        }

        if (selectedPeriod === selectedLesson) {
            resetFilters(dataState);
            return;
        }

        setSelectedLesson(selectedPeriod);
        setSelectedCallType(null);

        const filteredCalls = (dataState.onCallRecords || []).filter((callRecord) => (
            findSchoolPeriod(callRecord.submissionEpoch, dataState.timetableTimings) === selectedPeriod
        ));

        setFilteredDataState(filteredCalls);
        setAllCallsArrayState(filteredCalls);
        setTotalCallCountState(filteredCalls.length);
    }

    function handleCallTypeSelect(selectedType) {
        if (!dataState) {
            return;
        }

        if (selectedType === selectedCallType) {
            resetFilters(dataState);
            return;
        }

        setSelectedCallType(selectedType);
        setSelectedLesson(null);

        const filteredCalls = (dataState.onCallRecords || []).filter((callRecord) => (
            callRecord.callType === selectedType
        ));

        setFilteredDataState(filteredCalls);
        setAllCallsArrayState(filteredCalls);
        setTotalCallCountState(filteredCalls.length);
    }

    async function apiCall(params, isRefresh = false) {
        if (isRefresh) {
            setIsApplyingDateFilterState(true);
        }

        try {
            setApiErrorState(false);
            const response = await SEC_ApiCall('oncall/reports/pupil', params, setApiErrorState);
            const normalisedResponse = normaliseReportResponse(response);
            const onCallRecords = Array.isArray(normalisedResponse?.onCallRecords)
                ? [...normalisedResponse.onCallRecords].sort((a, b) => Number(b.submissionEpoch) - Number(a.submissionEpoch))
                : [];

            const cleanResponse = {
                ...normalisedResponse,
                onCallRecords,
                onCallType: Array.isArray(normalisedResponse?.onCallType) ? normalisedResponse.onCallType : [],
                timetableTimings: Array.isArray(normalisedResponse?.timetableTimings) ? normalisedResponse.timetableTimings : [],
                termDateRanges: normalisedResponse?.termDateRanges || {},
            };

            setDataState(cleanResponse);
            setFilteredDataState(onCallRecords);
            setAllCallsArrayState(onCallRecords);
            setTotalCallCountState(onCallRecords.length);
            setCallTypeColoursState(cleanResponse.onCallType);
            setSelectedLesson(null);
            setSelectedCallType(null);
        } catch (error) {
            console.error('Failed to load individual pupil OnCall report', error);
            setApiErrorState(true);
        } finally {
            setIsLoadedState(true);
            setIsApplyingDateFilterState(false);
        }
    }

    useEffect(() => {
        apiCall({
            pupilId,
            startDate: startDateState,
            endDate: endDateState,
        });
    }, [pupilId]);

    function applyDateFilter() {
        setSearchParams({
            startDate: startDateState,
            endDate: endDateState,
        });
        apiCall({
            pupilId,
            startDate: startDateState,
            endDate: endDateState,
        }, true);
    }

    function handleTermRangeChange(selectedRange) {
        setSelectedTermRangeState(selectedRange);

        if (!selectedRange) {
            setStartDateState(defaultDates.startDate);
            setEndDateState(defaultDates.endDate);
            setSearchParams({
                startDate: defaultDates.startDate,
                endDate: defaultDates.endDate,
            });
            apiCall({
                pupilId,
                startDate: defaultDates.startDate,
                endDate: defaultDates.endDate,
            }, true);
            return;
        }

        setStartDateState(selectedRange.startDate);
        setEndDateState(selectedRange.endDate);
        setSearchParams({
            startDate: selectedRange.startDate,
            endDate: selectedRange.endDate,
        });
        apiCall({
            pupilId,
            startDate: selectedRange.startDate,
            endDate: selectedRange.endDate,
        }, true);
    }

    function apiCallRefresh() {
        apiCall({
            pupilId,
            startDate: startDateState,
            endDate: endDateState,
        }, true);
    }

    if (!isLoadedState) {
        return (
            <div className='main_div'>
                <LoadingComp />
            </div>
        );
    }

    if (apiErrorState || !dataState) {
        return (
            <div className='main_div'>
                <ApiError />
            </div>
        );
    }

    return (
        <div className='main_div'>
            <div className='oncall_reports_groupSummary_headerRow'>
                <div className='oncall_reports_groupSummary_headerText'>
                    <div className='oncall_reports_individual_titleRow'>
                        <h1 className='oncall_reports_groupSummary_mainHeader'>OnCall Report - {dataState.pupilName}</h1>
                        <PupilGroupIcons pupilRecord={dataState} />
                    </div>
                    <h1 className='oncall_reports_groupSummary_subHeader'>Total Calls: {totalCallCountState}</h1>
                </div>
                <button
                    type='button'
                    className='oncall_reports_groupSummary_backButton'
                    onClick={() => navigate(backPath)}
                >
                    Back to Reports
                </button>
            </div>

            <OnCallReportDateFilter
                startDate={startDateState}
                endDate={endDateState}
                setStartDate={(value) => {
                    setSelectedTermRangeState(null);
                    setStartDateState(value);
                }}
                setEndDate={(value) => {
                    setSelectedTermRangeState(null);
                    setEndDateState(value);
                }}
                onApply={applyDateFilter}
                isLoading={isApplyingDateFilterState}
                termDateRanges={dataState?.termDateRanges}
                selectedTermRange={selectedTermRangeState}
                onTermRangeChange={handleTermRangeChange}
            />

            <div className='oncall_reports_individual_main_div'>
                <div className='oncall_reports_individual_summaryDiv'>
                    <div className='oncall_reports_individual_pieChartDiv'>
                        <h1 className='oncall_reports_individual_divHeader'>Call Types</h1>
                        <h1 className='oncall_reports_individual_divSubHeader'>Click to filter</h1>
                        <PieChartComponent
                            callsArray={allCallsArrayState}
                            callTypeColours={callTypeColoursState}
                            onCallTypeSelect={handleCallTypeSelect}
                        />
                    </div>

                    <div className='oncall_reports_individual_tableDiv'>
                        <h1 className='oncall_reports_individual_divHeader'>Lesson Times</h1>
                        <h1 className='oncall_reports_individual_divSubHeader'>Based on call submission time</h1>
                        <h1 className='oncall_reports_individual_divSubHeader'>Click a lesson to filter and click it again to reset</h1>
                        <LessonTimesCountTable
                            data={allCallsArrayState}
                            timetableTimings={dataState.timetableTimings}
                            onLessonSelect={handleLessonSelect}
                            selectedLesson={selectedLesson}
                        />
                    </div>
                </div>

                <div className='oncall_reports_individual_lineChartDiv'>
                    <h1 className='oncall_reports_individual_divHeader'>Weekly Call Trends</h1>
                    <h1 className='oncall_reports_individual_divSubHeader'>Date is the Monday of that week</h1>
                    <div className='oncall_reports_individual_lineChartInnerDiv'>
                        <WeeklyCallTrendsLineChart data={allCallsArrayState} />
                    </div>
                </div>

                <div className='oncall_calls_main_div'>
                    <div className='oncall_calls_complete_calls_div'>
                        {filteredDataState.map((item) => (
                            <OnCallCompleteCall
                                key={item.id}
                                data={item}
                                showPupilId={false}
                                callTypes={callTypeColoursState}
                                apiCall={apiCallRefresh}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
